import type { FC } from 'react'
import type React from 'react'
import { useCallback, useContext, useEffect, useState } from 'react'
import styled from 'styled-components'

import { Button, AlternateButton } from '@/components/DEPRECATED_UI/Button'
import SlideInSidebar from '@/components/DEPRECATED_UI/SlideInSidebar'
import TextAreaInput from '@/components/input/TextAreaInput'
import ctx from '@/context/AppContext'
import { graphQLQuery } from '@/hooks/hooks'
import { useCreateDataRequestMutation, useCancelDataRequestMutation } from '@/lib/apollo'
import type { DataRequestRecommendation, Recommendation } from '@/types/Recommendation'

import ActiveRequests from './ActiveRequests'
import CompletedRequests from './CompletedRequests'

interface Props {
  customerId: string
  applicationId: string
  subjectId: string
  subjectName?: string
  hasOutcome?: boolean
  onClose: () => void
}

const Container = styled.div`
  display: grid;
`

const StyledTextAreaInput = styled(TextAreaInput)`
  height: 200px;
`

const defaultMessageBody = `We were unable to complete an accurate assessment of your identification, and we require additional information to verify your right to rent in the UK.

To proceed, please follow the link below to complete the process. You will be asked to upload a valid identification document and take a live selfie for verification.

Please complete this as soon as possible to avoid any delays. If you have any questions or need assistance, feel free to add a comment in the journey and we will contact you back.`

const defaultMessageSubject = 'Action Required: Complete your ID Verification'

const DataRequestsSidebar: FC<Props> = ({
  customerId,
  applicationId,
  subjectId,
  subjectName,
  hasOutcome,
  onClose,
}) => {
  const [messageBody, setMessageBody] = useState(defaultMessageBody)

  const [activeRecommendations, setActiveRecommendations] = useState<
    DataRequestRecommendation[]
  >([])
  const [completedRecommendations, setCompletedRecommendations] = useState<
    DataRequestRecommendation[]
  >([])
  const [recommendationsLoading, setRecommendationsLoading] = useState(false)

  const { personId } = useContext(ctx)

  const [createDataRequest, { loading, error }] = useCreateDataRequestMutation()
  const [performCancelDataRequest, { loading: cancelLoading, error: cancelError }] =
    useCancelDataRequestMutation()

  const handleCancelRequest = async (
    recommendation: DataRequestRecommendation,
    notifyApplicant: boolean,
  ) => {
    try {
      await performCancelDataRequest({
        variables: {
          customerId,
          subjectId,
          userId: personId ?? '',
          recommendationType: 'IDVTRecommendation',
          recommendationId: recommendation.id,
          notifyApplicant,
        },
      })

      // Optimistically update the UI - remove from active list
      setActiveRecommendations(prev => prev.filter(item => item.id !== recommendation.id))
    } catch (err) {
      console.error('Failed to cancel request:', err)
    } finally {
      // Re-fetch the recommendations to ensure UI is up to date
      fetchRecommendations()
    }
  }

  const fetchRecommendations = useCallback(async () => {
    setRecommendationsLoading(true)

    const response = await graphQLQuery({
      query: `query{
          getRecommendationsForSubject(cusId: "${customerId}" appId: "${applicationId}" subId: "${subjectId}"){
              id
              category
              displayName
              description
              completed
              createdAt
              updatedAt
              requestMessage
              author
              status
            }
          }`,
    })

    const { data } = await response.json()

    if (data) {
      const recommendations: Recommendation[] = data.getRecommendationsForSubject

      const { active, completed } = recommendations.reduce(
        (acc, recommendation) => {
          if (recommendation.category === 'idvtRequest') {
            const processedRecommendation = {
              ...recommendation,
            } as DataRequestRecommendation

            if (recommendation.completed) {
              acc.completed.push(processedRecommendation)
            } else {
              acc.active.push(processedRecommendation)
            }
          }
          return acc
        },
        {
          active: [] as DataRequestRecommendation[],
          completed: [] as DataRequestRecommendation[],
        },
      )

      setActiveRecommendations(active)
      setCompletedRecommendations(completed)
    }

    setRecommendationsLoading(false)
  }, [customerId, applicationId, subjectId])

  useEffect(() => {
    fetchRecommendations()
  }, [fetchRecommendations])

  return (
    <SlideInSidebar
      onClose={onClose}
      header={<h1>IDVT Request</h1>}
      content={
        <Container>
          {!hasOutcome && (
            <div>
              <h2>Active IDVT Requests</h2>
              {recommendationsLoading ? (
                <div>Loading...</div>
              ) : (
                <ActiveRequests
                  recommendations={activeRecommendations}
                  onCancelRequest={handleCancelRequest}
                  cancelRequestLoading={cancelLoading}
                  cancelRequestError={cancelError}
                />
              )}
            </div>
          )}

          <div>
            <h2>Completed IDVT Requests</h2>
            {recommendationsLoading ? (
              <div>Loading...</div>
            ) : (
              <CompletedRequests recommendations={completedRecommendations} />
            )}
          </div>

          {!hasOutcome &&
            !recommendationsLoading &&
            activeRecommendations.length === 0 && (
              <>
                <h2>Request IDVT connection</h2>
                <div>
                  <p>
                    Subject: <b>{defaultMessageSubject}</b>
                  </p>
                </div>
                <div>
                  <p>Hi {subjectName}</p>
                  <StyledTextAreaInput
                    value={messageBody}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                      setMessageBody(e.target.value)
                    }
                  />
                  <p>Regards</p>
                  <p>Team Goodlord</p>
                </div>
                {error && <>An unexpected error occurred. {error?.message}</>}
                <div className="side-by-side-flex-end">
                  <AlternateButton onClick={onClose}>Cancel</AlternateButton>
                  <Button
                    disabled={loading || messageBody.trim() === ''}
                    onClick={() =>
                      createDataRequest({
                        variables: {
                          customerId,
                          subjectId,
                          personId: personId ?? '',
                          requestSubject: defaultMessageSubject,
                          requestMessage: messageBody,
                          recommendationType: 'IdvtRecommendation',
                        },
                      }).then(fetchRecommendations)
                    }
                  >
                    Send request
                  </Button>
                </div>
              </>
            )}
        </Container>
      }
    />
  )
}

export default DataRequestsSidebar
